import React from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/app/DatePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Trash, PaperPlaneTilt, CurrencyCircleDollar } from "@phosphor-icons/react";
import { fetcher, fmtN, todayISO } from "./common";

let lineSeq = 0;
const blankLine = () => ({ uid: `ln-${++lineSeq}-${Date.now()}`, description: "", qty: "1", unit_price: "", tva: true });

export default function Invoicing({ file }) {
  const key = `/accounting/files/${file.id}/invoices`;
  const { data: invoices = [] } = useSWR(key, fetcher);
  const [open, setOpen] = React.useState(false);
  const [client, setClient] = React.useState("");
  const [issueDate, setIssueDate] = React.useState(todayISO());
  const [lines, setLines] = React.useState([blankLine()]);

  const totals = lines.reduce((t, l) => {
    const amount = (parseFloat(l.qty) || 0) * (parseFloat(l.unit_price) || 0);
    return { ht: t.ht + amount, tva: t.tva + (l.tva ? amount * file.tva_rate / 100 : 0) };
  }, { ht: 0, tva: 0 });

  const setLine = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/accounting/files/${file.id}/invoices`, {
        client_name: client, issue_date: issueDate,
        lines: lines.filter((l) => l.description && parseFloat(l.unit_price)).map((l) => ({
          description: l.description, qty: parseFloat(l.qty) || 1, unit_price: parseFloat(l.unit_price), tva: l.tva,
        })),
      });
      toast.success("Facture créée en brouillon");
      setOpen(false); setClient(""); setLines([blankLine()]);
      mutate(key);
    } catch (err) { toast.error(err?.response?.data?.detail || "Création impossible"); }
  };

  const act = async (id, action, msg) => {
    try { await api.post(`/accounting/files/${file.id}/invoices/${id}/${action}`); toast.success(msg); mutate(key); }
    catch (err) { toast.error(err?.response?.data?.detail || "Action impossible"); }
  };
  const del = async (id) => {
    try { await api.delete(`/accounting/files/${file.id}/invoices/${id}`); toast.success("Facture supprimée"); mutate(key); }
    catch (err) { toast.error(err?.response?.data?.detail || "Suppression impossible"); }
  };

  const STATUS = { brouillon: "Brouillon", emise: "Émise", payee: "Payée" };
  const STATUS_STYLE = {
    brouillon: "bg-zinc-100 text-zinc-700",
    emise: "bg-amber-50 text-amber-800",
    payee: "bg-emerald-50 text-emerald-800",
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-zinc-600 max-w-xl">L'émission génère automatiquement l'écriture de vente (journal VE), l'encaissement l'écriture de banque (journal BQ).</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="new-invoice-btn"><Plus size={16} className="mr-1" /> Nouvelle facture</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-serif-display text-2xl">Nouvelle facture</DialogTitle>
              <DialogDescription>TVA au taux du dossier ({file.tva_rate}%).</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Client *</Label><Input required value={client} onChange={(e) => setClient(e.target.value)} data-testid="invoice-client" /></div>
                <div><Label>Date d'émission</Label><DatePicker value={issueDate} onChange={setIssueDate} testid="invoice-issue-date" /></div>
              </div>
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                  <div className="col-span-5">Description</div><div className="col-span-2">Qté</div><div className="col-span-2">PU HT</div><div className="col-span-2">TVA</div><div className="col-span-1"></div>
                </div>
                {lines.map((l, i) => (
                  <div key={l.uid} className="grid grid-cols-12 gap-2 items-center">
                    <Input className="col-span-5 h-8 text-sm" value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} data-testid={`invoice-line-${i}-desc`} placeholder="Prestation…" />
                    <Input className="col-span-2 h-8 text-sm" type="number" min="0" step="0.5" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} />
                    <Input className="col-span-2 h-8 text-sm" type="number" min="0" step="0.01" value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} data-testid={`invoice-line-${i}-pu`} />
                    <div className="col-span-2 flex items-center gap-1"><Checkbox checked={l.tva} onCheckedChange={(v) => setLine(i, "tva", !!v)} /><span className="text-xs text-zinc-500">{file.tva_rate}%</span></div>
                    <div className="col-span-1">{lines.length > 1 && <button type="button" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} className="text-zinc-400 hover:text-red-700"><Trash size={14} /></button>}</div>
                  </div>
                ))}
                <button type="button" onClick={() => setLines((ls) => [...ls, blankLine()])} className="text-xs text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1"><Plus size={12} /> Ajouter une ligne</button>
              </div>
              <div className="flex justify-between items-center border-t border-zinc-200 pt-3 text-sm">
                <div className="flex gap-6">
                  <div><span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 block">Total HT</span><span className="font-medium">{fmtN(totals.ht)}</span></div>
                  <div><span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 block">TVA</span><span className="font-medium">{fmtN(totals.tva)}</span></div>
                  <div><span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 block">Total TTC</span><span className="font-medium">{fmtN(totals.ht + totals.tva)}</span></div>
                </div>
                <Button type="submit" disabled={!client || totals.ht <= 0} className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="invoice-submit">Créer</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-zinc-200 bg-white rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500">
              <th className="px-4 py-2">N°</th><th className="px-4 py-2">Client</th><th className="px-4 py-2">Date</th>
              <th className="px-4 py-2 text-right">HT</th><th className="px-4 py-2 text-right">TVA</th><th className="px-4 py-2 text-right">TTC</th>
              <th className="px-4 py-2">Statut</th><th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {invoices.length === 0 && <tr><td colSpan={8} className="p-6 text-zinc-500">Aucune facture.</td></tr>}
            {invoices.map((inv) => (
              <tr key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                <td className="px-4 py-2 font-mono text-xs">{inv.number}</td>
                <td className="px-4 py-2 font-medium">{inv.client_name}</td>
                <td className="px-4 py-2">{inv.issue_date}</td>
                <td className="px-4 py-2 text-right">{fmtN(inv.total_ht)}</td>
                <td className="px-4 py-2 text-right">{fmtN(inv.total_tva)}</td>
                <td className="px-4 py-2 text-right font-medium">{fmtN(inv.total_ttc)}</td>
                <td className="px-4 py-2"><span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_STYLE[inv.status]}`}>{STATUS[inv.status]}</span></td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  {inv.status === "brouillon" && (
                    <>
                      <button onClick={() => act(inv.id, "issue", "Facture émise — écriture de vente générée")} className="text-zinc-600 hover:text-zinc-900 mr-2" title="Émettre" data-testid={`invoice-issue-${inv.id}`}><PaperPlaneTilt size={16} /></button>
                      <button onClick={() => del(inv.id)} className="text-zinc-400 hover:text-red-700" title="Supprimer"><Trash size={16} /></button>
                    </>
                  )}
                  {inv.status === "emise" && (
                    <button onClick={() => act(inv.id, "pay", "Encaissement comptabilisé en banque")} className="text-emerald-700 hover:text-emerald-900" title="Encaisser" data-testid={`invoice-pay-${inv.id}`}><CurrencyCircleDollar size={16} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
